import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { merge, get } from "lodash";
import { defaultErrorMessage } from "../../constants";
import { setCreditCard } from "../../Actions";
import ErrorModal from "../Shared/ErrorModal";
import LatestModalDesign from "../Shared/LatestModalDesign";
import LoadingOverlay from "../Shared/LoadingOverlay";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCVCElement,
  injectStripe,
} from "react-stripe-elements";
import "./AddNewPaymentMethod.css";
import { createCreditCardOfUser } from "../Shared/Helpers";

class AddNewPaymentMethod extends React.Component {
  constructor(props) {
    super(props);

    this.handleSave = this.handleSave.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.setCreditCard = this.setCreditCard.bind(this);
    this.closeErrorModal = this.closeErrorModal.bind(this);

    // Initialize credit card state with safety check
    this.props.setCreditCard(
      merge(
        {
          name: "",
          zipCode: "",
          stripe: this.props.stripe || null,
        },
        this.props.creditCard || {}
      )
    );

    this.state = {
      loading: false,
      showErrorModal: false,
      error: null,
    };
  }
  componentDidUpdate(prevProps) {
    // Update stripe reference if it becomes available
    if (this.props.stripe && this.props.stripe !== prevProps.stripe) {
      this.props.setCreditCard({
        ...this.props.creditCard,
        stripe: this.props.stripe,
      });
    }
  }

  componentWillUnmount() {
    // Clear form data but preserve stripe reference for next modal instance
    this.props.setCreditCard({
      name: "",
      zipCode: "",
      stripe: this.props.stripe, // Keep stripe reference, don't clear it
    });
  }

  // Safe setState that only executes if component is still mounted
  safeSetState(stateUpdate, callback) {
    this.setState(stateUpdate, callback);
  }

  setCreditCard(params) {
    this.props.setCreditCard(merge({}, this.props.creditCard, params));
  }

  handleClose() {
    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  handleSave(event) {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    // Safety checks before proceeding
    // if (!this._isMounted) {
    // return; // Component unmounted, abort operation
    // }

    // Use stripe from props if available, otherwise from creditCard state
    const stripeInstance = this.props.stripe || this.props.creditCard?.stripe;

    if (!stripeInstance) {
      this.safeSetState({
        showErrorModal: true,
        error: "Payment system not ready. Please try again.",
      });
      return;
    }

    this.safeSetState({ loading: true });

    const that = this;

    stripeInstance
      .createToken({
        name: this.props.creditCard.name,
        address_zip: this.props.creditCard.zipCode,
      })
      .then(function (result) {
        if (result && result.token) {
          const { card } = result.token;

          // Use the helper function with proper callback pattern
          createCreditCardOfUser(
            {
              last_4: card.last4,
              brand: card.brand,
              id: card.id,
              stripe_id: result.token.id,
              name: card.name,
              exp_month: card.exp_month,
              exp_year: card.exp_year,
              user_id: that.props.client.user_id,
            },
            // Success callback
            (response) => {
              that.safeSetState({ loading: false });
              const { id, success, result } = response.data;

              if (success || result) {
                // Call parent's success callback to refresh the list
                if (that.props.onSuccess) {
                  that.props.onSuccess(id);
                }
                // Close the modal
                that.handleClose();
              } else {
                const message = get(
                  response,
                  "data.errors.message",
                  defaultErrorMessage
                );
                that.safeSetState({ showErrorModal: true, error: message });
              }
            },
            // Error callback
            (error) => {
              that.safeSetState({ loading: false });
              if (error && error.response && error.response.data) {
                const message = get(
                  error.response.data,
                  "errors.0.message",
                  defaultErrorMessage
                );
                that.safeSetState({ showErrorModal: true, error: message });
              } else {
                that.safeSetState({
                  showErrorModal: true,
                  error: defaultErrorMessage,
                });
              }
            }
          );
        } else {
          that.safeSetState({ loading: false });
          const { error } = result;
          const message = error?.message || defaultErrorMessage;
          that.safeSetState({ showErrorModal: true, error: message });
        }
      })
      .catch(function (stripeError) {
        console.log("Stripe createToken error:", stripeError);
        that.safeSetState({
          loading: false,
          showErrorModal: true,
          error: "Unable to process payment information. Please try again.",
        });
      });
  }

  closeErrorModal() {
    this.safeSetState({ showErrorModal: false, error: null });
  }

  render() {
    const { isOpen } = this.props;
    const { loading, showErrorModal, error } = this.state;
    return (
      <>
        <LatestModalDesign
          isOpen={isOpen}
          title="Add Payment Method"
          close={this.handleClose}
          apply={this.handleSave}
          applyBtnCopy="Add Card"
          cancelBtnCopy="Cancel"
          disabled={loading}
          contentExtraClass="add-payment-method-modal"
        >
          <div className="row">
            <div className="col-xs-12 mb-16">
              <label className="size-14-20 contentPrimary font-weight-bold mb-8 d-block">
                Name on card
              </label>
              <input
                type="text"
                value={this.props.creditCard?.name || ""}
                className="form-control size-16-24 p-12"
                onChange={(event) =>
                  this.setCreditCard({ name: event.target.value })
                }
                required
              />
            </div>

            <div className="col-xs-12 mb-16">
              <label className="size-14-20 contentPrimary font-weight-bold mb-8 d-block">
                Card number
              </label>
              <CardNumberElement
                classes={{
                  base: "stripe-cardnumber-element",
                }}
                style={{
                  base: {
                    fontSize: "16px",
                    fontFamily: "Poppins",
                    fontWeight: "300",
                    color: "#2C3239",
                  },
                }}
              />
            </div>

            <div className="col-xs-6 mb-16">
              <label className="size-14-20 contentPrimary font-weight-bold mb-8 d-block">
                Expiration date
              </label>
              <CardExpiryElement
                classes={{
                  base: "stripe-expiration-element",
                }}
                style={{
                  base: {
                    fontSize: "16px",
                    fontFamily: "Poppins",
                    fontWeight: "300",
                    color: "#2C3239",
                  },
                }}
              />
            </div>

            <div className="col-xs-6 mb-16 position-relative">
              <label className="size-14-20 contentPrimary font-weight-bold mb-8 d-block">
                CVC
              </label>
              <CardCVCElement
                classes={{
                  base: "stripe-cvc-element",
                }}
                style={{
                  base: {
                    fontSize: "16px",
                    fontFamily: "Poppins",
                    fontWeight: "300",
                    color: "#2C3239",
                  },
                }}
              />
            </div>

            <div className="col-xs-6 mb-16">
              <label className="size-14-20 contentPrimary font-weight-bold mb-8 d-block">
                Zip code
              </label>
              <input
                type="text"
                value={this.props.creditCard?.zipCode || ""}
                className="form-control size-16-24 p-12"
                onChange={(event) =>
                  this.setCreditCard({ zipCode: event.target.value })
                }
                required
              />
            </div>
          </div>
          {/* </form> */}
        </LatestModalDesign>

        {showErrorModal && (
          <ErrorModal isOpen={showErrorModal} close={this.closeErrorModal}>
            <p>{error}</p>
          </ErrorModal>
        )}

        <LoadingOverlay showLoader={loading} />
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  client: state.client,
  creditCard: state.creditCard,
});

AddNewPaymentMethod.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  client: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  setCreditCard: PropTypes.func.isRequired,
  creditCard: PropTypes.object,
  stripe: PropTypes.object,
};

export default injectStripe(
  connect(mapStateToProps, { setCreditCard })(AddNewPaymentMethod)
);
